import { PARCEL_CATEGORIES } from "./parcelCategories";

export function formatCHF(amount) {
  if (amount == null || isNaN(amount)) return "—";
  return new Intl.NumberFormat("it-CH", { style: "currency", currency: "CHF" }).format(amount);
}

export function formatKm(km) {
  if (km == null) return "—";
  return `${Number(km).toFixed(1)} km`;
}

export function formatDateTime(value) {
  if (!value) return "—";
  try {
    const d = typeof value === "string" ? new Date(value) : value;
    return new Intl.DateTimeFormat("it-CH", { dateStyle: "medium", timeStyle: "short" }).format(d);
  } catch {
    return "—";
  }
}

export function relativeTime(value) {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  return `${Math.floor(diff / 86400)} g fa`;
}

export const STATUS_LABEL = {
  pending: "In attesa",
  // Phase 44.12 — Virtual status: ordine già pagato ma ancora senza driver.
  // Trasforma il vecchio "Da pagare"/"In attesa" misleading in un messaggio
  // di trust (Stripe/subscription/pack hanno coperto, manca solo il match).
  paid_matching: "Cerchiamo un driver",
  accepted: "Accettata",
  picking_up: "In ritiro",
  picked_up: "Ritirata",
  delivering: "In consegna",
  delivered: "Consegnata",
  cancelled: "Annullata",
  // Phase 43.18d — stati "virtuali" per ordini annullati con refund tracking
  cancelled_refund_pending: "Annullato · rimborso in attesa",
  cancelled_refunded: "Annullato e rimborsato",
  cancelled_refund_failed_customer: "Annullato · rimborso in elaborazione",
  cancelled_refund_failed_admin: "Annullato · refund failed",
  cancelled_no_refund: "Annullato",
  cancelled_credit_restored: "Annullato · credito restituito",
  cancelled_credit_pending: "Annullato · riaccredito in attesa",
  cancelled_review: "Annullato · credito da verificare",
};

export const STATUS_TONE = {
  pending: { bg: "bg-amber-50", fg: "text-amber-700", dot: "bg-amber-500" },
  // Phase 44.12 — Ordine pagato in attesa driver: blu soft = "in lavorazione".
  paid_matching: { bg: "bg-blue-50", fg: "text-blue-700", dot: "bg-blue-500" },
  accepted: { bg: "bg-blue-50", fg: "text-blue-700", dot: "bg-blue-500" },
  picking_up: { bg: "bg-violet-50", fg: "text-violet-700", dot: "bg-violet-500" },
  picked_up: { bg: "bg-violet-50", fg: "text-violet-700", dot: "bg-violet-500" },
  delivering: { bg: "bg-violet-50", fg: "text-violet-700", dot: "bg-violet-500" },
  delivered: { bg: "bg-emerald-50", fg: "text-emerald-700", dot: "bg-emerald-500" },
  cancelled: { bg: "bg-red-50", fg: "text-red-700", dot: "bg-red-500" },
  // Phase 43.18d — toni per stati cancelled con refund
  cancelled_refund_pending: { bg: "bg-amber-50", fg: "text-amber-700", dot: "bg-amber-500" },
  cancelled_refunded: { bg: "bg-emerald-50", fg: "text-emerald-700", dot: "bg-emerald-500" },
  cancelled_refund_failed_customer: { bg: "bg-amber-50", fg: "text-amber-700", dot: "bg-amber-500" },
  cancelled_refund_failed_admin: { bg: "bg-rose-100", fg: "text-rose-800", dot: "bg-rose-500" },
  cancelled_no_refund: { bg: "bg-zinc-100", fg: "text-zinc-700", dot: "bg-zinc-500" },
  cancelled_credit_restored: { bg: "bg-emerald-50", fg: "text-emerald-700", dot: "bg-emerald-500" },
  cancelled_credit_pending: { bg: "bg-amber-50", fg: "text-amber-700", dot: "bg-amber-500" },
  cancelled_review: { bg: "bg-amber-50", fg: "text-amber-700", dot: "bg-amber-500" },
};

/**
 * Risolve lo stato "virtuale" da mostrare in UI per un ordine.
 *
 * Phase 43.18d — quando un ordine è "cancelled" lo stato visibile dipende
 * dallo stato del refund e dal ruolo dell'audience (cliente vs admin).
 *
 * @param {{status, payment_status, refund_status}} order
 * @param {"customer"|"admin"|"driver"} audience  default "customer"
 * @returns string  uno dei chiave di STATUS_LABEL/STATUS_TONE
 */
export function resolveOrderStatus(order, audience = "customer") {
  if (!order) return "pending";
  // Phase 44.12 — Fix critico trust: ordine in `status=pending` ma con
  // pagamento già coperto (Stripe paid, subscription_full_credit, delivery_pack
  // o off-session charge ok) deve mostrarsi come "Cerchiamo un driver", non
  // come "In attesa"/"Da pagare". L'override è esposto al cliente (sapere che
  // ha già pagato) e all'admin (operativo: deve assegnare driver).
  if (order.status === "pending" && order.payment_status === "paid") {
    return "paid_matching";
  }
  if (order.status !== "cancelled") return order.status;
  // Ordine cancellato — distingui caso refund
  if (order.refund_review_required) return "cancelled_review";
  const creditOnly = Number(order.final_price ?? order.price ?? 0) === 0 &&
    ["delivery_pack", "subscription_full_credit", "wallet_credit_full"].includes(order.payment_source);
  if (creditOnly && order.payment_status === "paid") {
    return order.refund_status === "succeeded" ? "cancelled_credit_restored" : "cancelled_credit_pending";
  }
  const wasPaid = order.payment_status === "paid";
  if (!wasPaid) return "cancelled_no_refund";   // mai pagato → nulla da rimborsare
  const r = order.refund_status;
  if (r === "succeeded") return "cancelled_refunded";
  if (r === "failed") {
    return audience === "admin" ? "cancelled_refund_failed_admin" : "cancelled_refund_failed_customer";
  }
  // pending o assente
  return "cancelled_refund_pending";
}

export const SIZE_LABEL = Object.fromEntries(PARCEL_CATEGORIES.map(p => [p.id, p.id === "small" ? "Small" : p.label]));

export const DELIVERY_TYPE_LABEL = {
  shared: "Shared",
  standard: "Standard",
  express: "Express",
  van: "Van",
};

export function expressServiceDescription(slaMinutes = 120) {
  const minutes = Number.isInteger(slaMinutes) && slaMinutes > 0 ? slaMinutes : 120;
  return `Entro ${minutes} minuti dal ritiro, dopo verifica di tratta e disponibilità e conferma del team. Solo nella fascia 06:30–21:00: la consegna deve concludersi entro le 21:00`;
}

export const DELIVERY_TYPE_DESC = {
  shared: "Servizio per driver privati · In arrivo, non ancora prenotabile",
  standard: "Consegna in giornata con flotta interna: entro le 21:00 per ritiri entro le 18:00",
  express: expressServiceDescription(),
  van: "Su richiesta · verifica preventiva. Max 25 kg e 120 × 80 × 80 cm per collo, imballaggio incluso",
};

export const URGENCY_LABEL = {
  standard: "Standard",
  express: "Express",
  priority: "Priority",
};

export const PLAN_LABEL = {
  free: "Free",
  business_start: "Business Start",
  business_pro: "Business Pro",
  enterprise: "Enterprise",
};

export const ROLE_LABEL = {
  customer_private: "Privato",
  customer_business: "Azienda",
  customer_shop: "Negozio",
  driver: "Trasportatore",
  admin: "Admin",
};

export const STATUS_STEPS = [
  "pending",
  "accepted",
  "picking_up",
  "picked_up",
  "delivering",
  "delivered",
];
